import { Linkedin, Twitter, Facebook, MessageCircle, Instagram, Link as LinkIcon, Download, Check, Share2 } from "lucide-react";
import { useState, useCallback } from "react";
import { useT } from "@/i18n/LanguageContext";
import { trackCta } from "@/lib/analytics";

/**
 * Share actions for a Stoic Training Card.
 *
 *   • Download — saves the original 1080×1080 PNG
 *   • Native share — uses Web Share API when available (mobile, can attach the file)
 *   • LinkedIn / X / Facebook / WhatsApp — open the platform's share intent
 *     with the card's public permalink + a one-line caption
 *   • Instagram — no public share intent exists; we download the image and
 *     show a short instructive toast so the user can post manually
 *   • Copy link — copies the permalink to clipboard
 *
 * Props:
 *   card  — entry from /data/trainingCards.js
 *   imageUrl — absolute URL of the PNG (e.g. https://stoicos.ai/cards/file.png)
 *   shareUrl — absolute URL of the share permalink
 */
async function fetchAsFile(url, filename) {
  const res = await fetch(url, { cache: "no-store" });
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
}

function PlatformButton({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-label={label}
      title={label}
      className="group inline-flex items-center justify-center w-11 h-11 border border-gold/40 hover:border-gold hover:bg-gold/10 transition-colors"
    >
      <Icon size={18} strokeWidth={1.5} className="text-gold" />
    </button>
  );
}

export default function ShareActions({ card, imageUrl, shareUrl }) {
  const { t, lang } = useT();
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [hint, setHint] = useState("");

  const caption =
    lang === "nl"
      ? `${card.quote.nl} (${card.attribution})`
      : `${card.quote.en} (${card.attribution})`;

  const onDownload = useCallback(async () => {
    trackCta("card_download", { slug: card.slug });
    // Force-download by going through a blob (works even if the server omits Content-Disposition).
    try {
      const res = await fetch(imageUrl, { cache: "no-store" });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = card.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      setDownloaded(true);
      window.setTimeout(() => setDownloaded(false), 2000);
    } catch {
      // Fallback: open the PNG in a new tab so the user can right-click → save
      window.open(imageUrl, "_blank", "noopener,noreferrer");
    }
  }, [imageUrl, card]);

  const onNativeShare = useCallback(async () => {
    trackCta("card_share_native", { slug: card.slug });
    try {
      // Prefer file share (works on iOS/Android into Instagram, WhatsApp, etc.)
      if (navigator.canShare) {
        const file = await fetchAsFile(imageUrl, card.filename);
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: card.attribution, text: caption, url: shareUrl });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ title: card.attribution, text: caption, url: shareUrl });
      }
    } catch {
      // user cancelled or browser refused; no-op
    }
  }, [imageUrl, shareUrl, card, caption]);

  const openSocial = (url, platform) => {
    trackCta("card_share", { slug: card.slug, platform });
    window.open(url, "_blank", "noopener,noreferrer,width=620,height=620");
  };

  const onLinkedIn = () =>
    openSocial(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      "linkedin"
    );

  const onX = () =>
    openSocial(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`,
      "x"
    );

  const onFacebook = () =>
    openSocial(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "facebook"
    );

  const onWhatsApp = () =>
    openSocial(
      `https://wa.me/?text=${encodeURIComponent(`${caption} ${shareUrl}`)}`,
      "whatsapp"
    );

  const onInstagram = useCallback(async () => {
    trackCta("card_share", { slug: card.slug, platform: "instagram" });
    await onDownload();
    setHint(t("cards.share.instagramHint"));
    window.setTimeout(() => setHint(""), 4500);
  }, [onDownload, t, card]);

  const onCopyLink = useCallback(async () => {
    trackCta("card_copy_link", { slug: card.slug });
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // ignore — older browsers
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }, [shareUrl, card]);

  return (
    <div className="flex flex-col gap-4" data-testid={`card-share-actions-${card.slug}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onDownload}
          data-testid={`card-download-${card.slug}`}
          className="inline-flex items-center gap-2.5 px-5 py-3 bg-gold text-ink hover:bg-gold-hover transition-colors text-[11px] tracking-[0.22em] uppercase font-medium"
        >
          {downloaded ? <Check size={14} strokeWidth={1.75} /> : <Download size={14} strokeWidth={1.75} />}
          {downloaded ? t("cards.share.downloaded") : t("cards.share.download")}
        </button>

        {typeof navigator !== "undefined" && navigator.share && (
          <button
            type="button"
            onClick={onNativeShare}
            data-testid={`card-share-native-${card.slug}`}
            className="inline-flex items-center gap-2.5 px-5 py-3 border border-gold/50 text-gold hover:bg-gold/10 hover:border-gold transition-colors text-[11px] tracking-[0.22em] uppercase"
          >
            <Share2 size={14} strokeWidth={1.5} /> {t("cards.share.native")}
          </button>
        )}
      </div>

      <div className="pt-3 border-t border-white/10">
        <p className="text-[10px] tracking-[0.3em] uppercase text-fog/80 mb-3">
          {t("cards.share.shareTo")}
        </p>
        <div className="flex flex-wrap gap-2.5">
          <PlatformButton icon={Linkedin}      label="LinkedIn"  onClick={onLinkedIn}  testid={`card-share-linkedin-${card.slug}`} />
          <PlatformButton icon={Twitter}       label="X"         onClick={onX}         testid={`card-share-x-${card.slug}`} />
          <PlatformButton icon={Facebook}      label="Facebook"  onClick={onFacebook}  testid={`card-share-facebook-${card.slug}`} />
          <PlatformButton icon={MessageCircle} label="WhatsApp"  onClick={onWhatsApp}  testid={`card-share-whatsapp-${card.slug}`} />
          <PlatformButton icon={Instagram}     label="Instagram" onClick={onInstagram} testid={`card-share-instagram-${card.slug}`} />
          <PlatformButton
            icon={copied ? Check : LinkIcon}
            label={t("cards.share.copyLink")}
            onClick={onCopyLink}
            testid={`card-share-copy-${card.slug}`}
          />
        </div>
        {copied && (
          <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-gold" data-testid="card-share-copied-toast">
            {t("cards.share.linkCopied")}
          </p>
        )}
        {hint && (
          <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-gold" data-testid="card-share-hint">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}
